/* Progressive loader for the try-on look library.
   The first eight cards are server-rendered. This file hydrates their images
   around the visible carousel range, then adds the interactive library near
   the viewport and keeps the static cards usable if a deferred asset fails. */
(function () {
  'use strict';
  var roots = document.querySelectorAll('[data-braided-try-on]');
  if (!roots.length) return;
  if (window.__bwtBootLoaded) return;
  window.__bwtBootLoaded = true;

  var MARK_PREFIX = 'tryon:';
  var stylePromises = {};
  var scriptPromises = {};
  var dataPromises = {};
  var TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  var MAIN_WIDTHS = [400, 500, 900, 1200];
  var THUMB_WIDTHS = [320, 640];

  function mark(name) {
    try {
      if (window.performance && performance.mark) performance.mark(MARK_PREFIX + name);
    } catch (e) {}
  }

  function sizedImageUrl(src, width) {
    if (!src || !width || /^(data|blob):/i.test(src)) return src;
    if (src.indexOf('/cdn/shop/') === -1 && src.indexOf('/s/files/') === -1) return src;
    var hash = '';
    var hashIndex = src.indexOf('#');
    if (hashIndex !== -1) {
      hash = src.slice(hashIndex);
      src = src.slice(0, hashIndex);
    }
    if (/([?&])width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + width) + hash;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width + hash;
  }

  function imageSrcset(src, widths) {
    if (!src) return '';
    return (widths || []).map(function (width) {
      return sizedImageUrl(src, width) + ' ' + width + 'w';
    }).join(', ');
  }

  function loadStyle(url) {
    if (!url) return Promise.resolve();
    if (stylePromises[url]) return stylePromises[url];
    stylePromises[url] = new Promise(function (resolve, reject) {
      var selector = 'link[data-bwt-style="' + url.replace(/"/g, '\\"') + '"]';
      if (document.querySelector(selector)) { resolve(); return; }
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-bwt-style', url);
      link.onload = function () { resolve(); };
      link.onerror = function () { reject(new Error('style:' + url)); };
      document.head.appendChild(link);
    });
    return stylePromises[url];
  }

  function loadScript(url) {
    if (!url) return Promise.resolve();
    if (scriptPromises[url]) return scriptPromises[url];
    scriptPromises[url] = new Promise(function (resolve, reject) {
      var selector = 'script[data-bwt-script="' + url.replace(/"/g, '\\"') + '"]';
      if (document.querySelector(selector)) { resolve(); return; }
      var script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-bwt-script', url);
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('script:' + url)); };
      document.head.appendChild(script);
    });
    return scriptPromises[url];
  }

  function readJson(node, fallback) {
    try { return node && node.textContent ? JSON.parse(node.textContent) : fallback; }
    catch (e) { return fallback; }
  }

  function imageData(image, alt) {
    var img = image || {};
    var src = img.src || '';
    if (!src) return null;
    return {
      src: src,
      thumb: sizedImageUrl(src, 320),
      srcset: imageSrcset(src, MAIN_WIDTHS),
      thumbSrcset: imageSrcset(src, THUMB_WIDTHS),
      width: img.width || 0,
      height: img.height || 0,
      alt: alt == null ? (img.alt || '') : alt
    };
  }

  function optionNames(root) {
    var options = readJson(root.querySelector('script[data-bwt-options]'), []);
    if (!Array.isArray(options)) return [];
    return options.map(function (option) { return String(option && option.name ? option.name : option || ''); });
  }

  function normalizeVariants(variants, names) {
    return (Array.isArray(variants) ? variants : []).map(function (variant) {
      var values = Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3];
      var color = null, length = '', pack = '', density = null, cap = null;
      var dims = [];
      names.forEach(function (name, index) {
        var lower = String(name || '').toLowerCase();
        var value = values[index];
        if (value == null || value === '') return;
        if (lower.indexOf('cap') !== -1 || lower.indexOf('circumference') !== -1) { cap = value; return; }
        dims.push({ name: name, value: value });
        if (lower.indexOf('density') !== -1) density = value;
        else if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1 || lower.indexOf('shade') !== -1) color = value;
        else if (lower.indexOf('length') !== -1 || lower.indexOf('inch') !== -1) length = value;
        else if (lower.indexOf('pack') !== -1 || lower.indexOf('qty') !== -1 || lower.indexOf('quantity') !== -1 || lower.indexOf('bundle') !== -1) pack = value;
      });
      if (!dims.length && Array.isArray(variant.options)) {
        dims = variant.options.map(function (value, index) {
          var name = names[index] || ('Option ' + (index + 1));
          var lower = String(name || '').toLowerCase();
          if (value == null || value === '' || lower.indexOf('cap') !== -1 || lower.indexOf('circumference') !== -1) return null;
          return { name: name, value: value };
        }).filter(Boolean);
      }
      var image = imageData(variant.featured_image || variant.image || null, variant.featured_image && variant.featured_image.alt);
      return {
        id: variant.id,
        sku: variant.sku || '',
        color: color,
        length: length,
        pack: pack,
        density: density,
        cap: cap,
        dims: dims,
        price: variant.price,
        available: variant.available !== false,
        imageAlt: variant.featured_image ? (variant.featured_image.alt || '') : null,
        image: image ? image.src : null,
        thumb: image ? image.thumb : null,
        srcset: image ? image.srcset : '',
        thumbSrcset: image ? image.thumbSrcset : '',
        width: image ? image.width : 0,
        height: image ? image.height : 0
      };
    }).filter(function (variant) { return variant.id != null; });
  }

  function prepareData(root) {
    var handle = root.getAttribute('data-product-handle') || '';
    var kind = root.getAttribute('data-product-type') || '';
    if (!handle) return Promise.resolve(true);
    if (dataPromises[handle]) return dataPromises[handle];
    var names = optionNames(root);
    var seed = readJson(root.querySelector('script[data-bwt-variants]'), []);
    var theme = [];
    Array.prototype.forEach.call(document.querySelectorAll('script[data-variants]'), function (script) {
      var parsed = readJson(script, []);
      if (Array.isArray(parsed) && parsed.length > theme.length) theme = parsed;
    });
    // Full media/alt data is required to match mannequin references for wigs.
    // It is fetched only once the library is near the viewport, never on first paint.
    var productPromise = kind === 'wig'
      ? Promise.race([
        fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(function (response) { return response.ok ? response.json() : null; })
          .catch(function () { return null; }),
        new Promise(function (resolve) { window.setTimeout(function () { resolve(null); }, 5000); })
      ])
      : Promise.resolve(null);
    dataPromises[handle] = productPromise.then(function (product) {
      if (window.__bwtDataReady) return true;
      window.__bwtDataReady = true;
      var raw = product && Array.isArray(product.variants) ? product.variants : theme;
      var normalized = raw && raw.length && names.length ? normalizeVariants(raw, names) : [];
      window.__bwtVariants = normalized.length ? normalized : (Array.isArray(seed) ? seed : []);
      var media = product && Array.isArray(product.media) ? product.media : [];
      var images = product && Array.isArray(product.images) ? product.images : [];
      window.__bwtMedia = media.map(function (item) { return imageData(item); }).filter(Boolean);
      if (!window.__bwtMedia.length) {
        window.__bwtMedia = images.map(function (image) {
          return imageData(typeof image === 'string' ? { src: image } : image);
        }).filter(Boolean);
      }
      if (!window.__bwtMedia.length) {
        var seen = {};
        window.__bwtMedia = (window.__bwtVariants || []).map(function (variant) {
          if (!variant.image || seen[variant.image]) return null;
          seen[variant.image] = true;
          return {
            src: variant.image,
            thumb: variant.thumb || sizedImageUrl(variant.image, 320),
            srcset: variant.srcset || imageSrcset(variant.image, MAIN_WIDTHS),
            thumbSrcset: variant.thumbSrcset || imageSrcset(variant.image, THUMB_WIDTHS),
            width: variant.width || 0,
            height: variant.height || 0,
            alt: variant.imageAlt || ''
          };
        }).filter(Boolean);
      }
      return true;
    });
    return dataPromises[handle];
  }

  function statusFor(root) {
    return root.querySelector('[data-bwt="libraryStatus"]');
  }

  function showFallback(root, text) {
    var status = statusFor(root);
    if (!status) return;
    status.textContent = text;
    status.hidden = false;
  }

  function loadLibrary(root) {
    if (root.__bwtLibraryPromise) return root.__bwtLibraryPromise;
    if (root.getAttribute('data-bwt-state') === 'ready') return Promise.resolve(true);
    root.setAttribute('data-bwt-state', 'loading');
    mark('library:start');
    root.__bwtLibraryPromise = Promise.all([
      loadStyle(root.getAttribute('data-bwt-library-css')),
      prepareData(root)
    ])
      .then(function () { return loadScript(root.getAttribute('data-bwt-library-js')); })
      .then(function () {
        // The library marks the host ready after its delegated controls are bound.
        if (root.getAttribute('data-bwt-state') !== 'ready') throw new Error('library did not initialize');
        var status = statusFor(root);
        if (status) status.hidden = true;
        return true;
      })
      .catch(function (error) {
        root.setAttribute('data-bwt-state', 'failed');
        root.__bwtLibraryPromise = null;
        mark('library:failed');
        showFallback(root, 'Filters and Try-On are temporarily unavailable. You can still browse these looks and open My Try-On Gallery.');
        try { console.warn('[bwt] library failed', error); } catch (e) {}
        return false;
      });
    return root.__bwtLibraryPromise;
  }

  function replayAfterLoad(root, target, eventType) {
    var value = target && target.value;
    var action = target && target.getAttribute ? {
      tag: target.tagName,
      text: (target.textContent || '').trim(),
      cardIndex: (function () {
        var card = target.closest && target.closest('[data-index]');
        return card ? card.getAttribute('data-index') : null;
      })(),
      attrs: ['data-try-on', 'data-gallery', 'data-compare-look', 'data-buy', 'data-cap-shop', 'data-feather-buy', 'data-tutorial', 'data-length-filter', 'data-pack-filter'].reduce(function (out, name) {
        if (target.hasAttribute(name)) out[name] = target.getAttribute(name);
        return out;
      }, {})
    } : null;
    loadLibrary(root).then(function (ok) {
      if (!ok || !action) return;
      var current = null;
      if (action.cardIndex != null) {
        var sameCard = root.querySelector('.look-card[data-index="' + action.cardIndex + '"]');
        if (sameCard) {
          [ 'data-try-on', 'data-gallery', 'data-compare-look', 'data-buy', 'data-cap-shop', 'data-feather-buy', 'data-tutorial' ].some(function (name) {
            if (!action.attrs[name]) return false;
            current = sameCard.querySelector('[' + name + '="' + action.attrs[name] + '"]');
            return !!current;
          });
        }
      } else {
        Object.keys(action.attrs).some(function (name) {
          Array.prototype.some.call(root.querySelectorAll('[' + name + ']'), function (node) {
            if (node.getAttribute(name) === action.attrs[name]) { current = node; return true; }
            return false;
          });
          return !!current;
        });
      }
      if (!current && action.tag === 'SELECT') current = root.querySelector('[data-bwt="colorFilter"]');
      if (!current && action.tag === 'BUTTON') {
        Array.prototype.some.call(root.querySelectorAll('.look-filters button'), function (node) {
          if ((node.textContent || '').trim() === action.text) { current = node; return true; }
          return false;
        });
      }
      if (!current) return;
      if (eventType === 'change' && current.tagName === 'SELECT') {
        if (value != null) current.value = value;
        current.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (current.hasAttribute('data-try-on') && current.getAttribute('aria-disabled') === 'true') return;
      current.click();
    });
  }

  function interactionSelector(target) {
    if (!target || !target.closest) return null;
    return target.closest('[data-try-on],[data-gallery],[data-compare-look],[data-buy],[data-cap-shop],[data-feather-buy],[data-tutorial],.look-filters button,[data-bwt="colorFilter"]');
  }

  function lazyCardImages(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.look-card .look-image img[data-src]'));
  }

  function hydrateCardImage(root, img, priority) {
    if (!img || !img.getAttribute('data-src')) return;
    img.loading = 'eager';
    img.setAttribute('fetchpriority', priority || 'auto');
    if (img.getAttribute('data-sizes')) img.sizes = img.getAttribute('data-sizes');
    if (img.getAttribute('data-srcset')) img.srcset = img.getAttribute('data-srcset');
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');
    if (img.__bwtLoadBound) return;
    img.__bwtLoadBound = true;
    img.addEventListener('load', function () {
      img.setAttribute('data-bwt-card-loaded', '1');
      if (!root.__bwtVisibleLoaded) {
        root.__bwtVisibleLoaded = true;
        mark('visible-loaded');
      }
    }, { once: true });
  }

  function hydrateAllCards(root, priority) {
    var images = lazyCardImages(root);
    if (!images.length) return;
    if (!root.__bwtCardsVisible) {
      root.__bwtCardsVisible = true;
      mark('cards-visible');
    }
    images.forEach(function (img) { hydrateCardImage(root, img, priority || 'low'); });
  }

  function hydrateCardRange(root) {
    var carousel = root.querySelector('[data-bwt="lookCarousel"]');
    if (!carousel) {
      hydrateAllCards(root, 'low');
      return;
    }
    var carouselRect = carousel.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
    if (carouselRect.bottom < -300 || carouselRect.top > viewport + 300) return;
    var cards = Array.prototype.slice.call(root.querySelectorAll('.look-card'));
    var cardWidth = cards[0] ? cards[0].offsetWidth : 160;
    if (carouselRect.width < 2 || cardWidth < 2) {
      if (!root.__bwtRangeTimer) {
        root.__bwtRangeTimer = window.setTimeout(function () {
          root.__bwtRangeTimer = 0;
          hydrateCardRange(root);
        }, 100);
      }
      return;
    }
    var lookahead = Math.max(120, cardWidth);
    var hydrated = false;
    cards.forEach(function (card) {
      var img = card.querySelector('.look-image img[data-src]');
      if (!img) return;
      var cardRect = card.getBoundingClientRect();
      var visible = cardRect.right > carouselRect.left && cardRect.left < carouselRect.right;
      var near = cardRect.right > carouselRect.left - lookahead && cardRect.left < carouselRect.right + lookahead;
      if (!visible && !near) return;
      hydrateCardImage(root, img, visible ? 'high' : 'low');
      hydrated = true;
    });
    if (hydrated && !root.__bwtCardsVisible) {
      root.__bwtCardsVisible = true;
      mark('cards-visible');
    }
  }

  function observeCardImages(root) {
    var carousel = root.querySelector('[data-bwt="lookCarousel"]');
    if (!carousel) {
      hydrateAllCards(root, 'low');
      return;
    }
    if (root.__bwtCardObserver) root.__bwtCardObserver.disconnect();
    var images = lazyCardImages(root);
    if (!images.length) return;
    var pendingAtStart = images.length;
    if (!('IntersectionObserver' in window)) {
      hydrateAllCards(root, 'low');
      return;
    }
    var observer = new IntersectionObserver(function () {
      hydrateCardRange(root);
    }, { root: carousel, rootMargin: '0px 300px' });
    root.__bwtCardObserver = observer;
    images.forEach(function (img) { observer.observe(img); });
    if (!carousel.__bwtRangeBound) {
      carousel.__bwtRangeBound = true;
      carousel.addEventListener('scroll', function () {
        if (root.__bwtRangeRaf) return;
        root.__bwtRangeRaf = window.requestAnimationFrame(function () {
          root.__bwtRangeRaf = 0;
          hydrateCardRange(root);
        });
      }, { passive: true });
    }
    hydrateCardRange(root);
    if (root.__bwtCardFallbackTimer) window.clearTimeout(root.__bwtCardFallbackTimer);
    root.__bwtCardFallbackTimer = window.setTimeout(function () {
      var rect = root.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      if (rect.bottom < -300 || rect.top > viewport + 300) return;
      if (!root.__bwtCardsVisible && lazyCardImages(root).length >= pendingAtStart) hydrateAllCards(root, 'low');
    }, 2500);
  }

  window.JalizaBwtImages = {
    placeholder: TRANSPARENT_PIXEL,
    resizeUrl: sizedImageUrl,
    srcset: imageSrcset,
    observe: observeCardImages,
    hydrateAll: hydrateAllCards
  };

  roots.forEach(function (root) {
    if (root.hidden) return;
    var status = statusFor(root);
    if (status) status.hidden = true;

    var carousel = root.querySelector('[data-bwt="lookCarousel"]');
    var previous = root.querySelector('[data-bwt="lookPrevious"]');
    var next = root.querySelector('[data-bwt="lookNext"]');
    if (previous && carousel && !previous.__bwtScrollBound) {
      previous.__bwtScrollBound = true;
      previous.addEventListener('click', function () { carousel.scrollBy({ left: -carousel.clientWidth * 0.8, behavior: 'smooth' }); });
    }
    if (next && carousel && !next.__bwtScrollBound) {
      next.__bwtScrollBound = true;
      next.addEventListener('click', function () { carousel.scrollBy({ left: carousel.clientWidth * 0.8, behavior: 'smooth' }); });
    }

    root.addEventListener('click', function (event) {
      if (root.getAttribute('data-bwt-state') === 'ready') {
        var readyTarget = interactionSelector(event.target);
        if (readyTarget && readyTarget.hasAttribute('data-try-on') && readyTarget.getAttribute('aria-disabled') === 'true') {
          showFallback(root, 'This combination is not available for Try-On.');
        }
        return;
      }
      var target = interactionSelector(event.target);
      if (!target) return;
      if (target.tagName === 'SELECT') { loadLibrary(root); return; }
      event.preventDefault();
      event.stopPropagation();
      replayAfterLoad(root, target, 'click');
    }, true);

    root.addEventListener('change', function (event) {
      if (root.getAttribute('data-bwt-state') === 'ready') return;
      var target = event.target && event.target.closest ? event.target.closest('[data-bwt="colorFilter"]') : null;
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      replayAfterLoad(root, target, 'change');
    }, true);

    var fallbackTimer = window.setTimeout(function () {
      if (root.getAttribute('data-bwt-state') === 'ready') return;
      showFallback(root, 'You can browse these looks while filters finish loading.');
    }, 4000);
    root.__bwtFallbackTimer = fallbackTimer;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.disconnect();
          observeCardImages(root);
          loadLibrary(root);
          break;
        }
      }, { rootMargin: '300px 0px' });
      observer.observe(root);
    } else {
      observeCardImages(root);
      window.setTimeout(function () { loadLibrary(root); }, 0);
    }
  });

  function flushEarlyQueue() {
    var queue = window.__bwtEarlyQueue;
    if (!queue || !queue.length) return;
    window.__bwtEarlyQueue = [];
    queue.forEach(function (item) {
      if (!item || !item.root || !item.target) return;
      replayAfterLoad(item.root, item.target, item.type || 'click');
    });
  }
  window.__bwtFlushEarlyQueue = flushEarlyQueue;
  flushEarlyQueue();
})();
